import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { API_BASE_URL } from '../../config';
import './embed.css';

const humanizeType = (type) => {
  if (!type) return 'Event';
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const EmbedEventsTable = () => {
  const [searchParams] = useSearchParams();
  const bg = searchParams.get('bg') || '#0a0a0a';
  const limit = parseInt(searchParams.get('limit')) || 100;

  const refreshInterval = parseInt(searchParams.get('refresh')) || 10000;

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const socketRef = useRef(null);

  const fetchEvents = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/events?limit=${limit}`);
      if (res.ok) {
        const data = await res.json();
        setEvents((Array.isArray(data) ? data : []).slice(0, limit));
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchEvents();

    socketRef.current = io(API_BASE_URL, { auth: { public: true } });
    socketRef.current.on('market-event', (event) => {
      setEvents(prev => [{ ...event, isNew: true, timestamp: event.timestamp || new Date().toISOString() }, ...prev].slice(0, limit));
    });

    // Polling fallback
    const interval = setInterval(fetchEvents, refreshInterval);

    return () => {
      if (socketRef.current) socketRef.current.disconnect();
      clearInterval(interval);
    };
  }, [limit, refreshInterval]);

  const formatTime = (ts) => {
    if (!ts) return '--';
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const getImpact = (event) => {
    if (event.multiplier && event.multiplier !== 1) {
      const pct = ((event.multiplier - 1) * 100).toFixed(1);
      return { text: `${pct > 0 ? '+' : ''}${pct}%`, positive: pct > 0 };
    }
    if (event.changePercent != null) {
      return { text: `${event.changePercent > 0 ? '+' : ''}${event.changePercent}%`, positive: event.changePercent > 0 };
    }
    return { text: '--', positive: true };
  };

  const getAffected = (event) => {
    if (event.affectedStocks === 'ALL' || event.affectedStocks === 'all') return 'ALL STOCKS';
    if (Array.isArray(event.affectedStocks)) {
      return event.affectedStocks.map(s => typeof s === 'object' ? s.ticker : s).join(', ');
    }
    if (event.ticker) return event.ticker;
    return '--';
  };

  return (
    <div className="embed-table-page" style={{ background: bg === 'transparent' ? 'transparent' : bg }}>
      <div className="embed-table-header">
        <span className="embed-table-title">MARKET EVENTS</span>
        <span className="embed-table-live-dot" />
        <span className="embed-table-count">{events.length}</span>
        <button className="embed-fullscreen-btn" onClick={() => document.documentElement.requestFullscreen?.()}>
          &#x26F6;
        </button>
      </div>
      <div className="embed-table-wrap">
        <table className="embed-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Type</th>
              <th>Description</th>
              <th>Affected</th>
              <th>Impact</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: '#666' }}>Loading...</td></tr>
            ) : events.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: '#666' }}>No events yet</td></tr>
            ) : (
              events.map((event, i) => {
                const impact = getImpact(event);
                return (
                  <tr key={event.id || i} className={event.isNew ? 'embed-row-new' : ''}>
                    <td className="embed-td-time">{formatTime(event.timestamp)}</td>
                    <td className="embed-td-type">{humanizeType(event.type)}</td>
                    <td className="embed-td-desc">{event.description || event.headline || humanizeType(event.type)}</td>
                    <td className="embed-td-affected">{getAffected(event)}</td>
                    <td className={`embed-td-impact ${impact.positive ? 'positive' : 'negative'}`}>{impact.text}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EmbedEventsTable;
